import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import ModalSalvarVenda from '../pdv/ModalSalvarVenda';
import ModalCliente from '../pdv/ModalCliente';
import { hojeBR, somarDias } from '../../utils/data';

const parsePT = v => parseFloat(String(v).replace(',', '.')) || 0;

/**
 * Modal de pagamento autocontido — mesma experiência do F9 do PDV
 * (desconto final, múltiplas formas, bandeira de cartão, conta PIX,
 * banco do boleto, parcelas de crediário e atalhos de teclado).
 *
 * Props:
 *  - aberto        bool
 *  - onFechar      () => void
 *  - totalVenda    number — total a pagar (antes do desconto do modal)
 *  - cliente       { id, nome } | null — para validação de crediário
 *  - tipoMovFiscal objeto do tipo de movimento (gera_financeiro) | null
 *  - onConfirmar   async ({ desconto, total_pago, troco, pagamentos }) => void
 *                  pagamentos: [{ forma_pagamento, valor, bandeira_id, parcelas, conta_id }]
 *                  Erros lançados aparecem no rodapé do modal.
 *  - onClienteChange (clienteObj | null) => void — quando informado, habilita o
 *                  F10 dentro do modal para escolher/trocar o cliente da venda
 *                  (necessário p/ crediário quando a venda começou sem cliente).
 */
export default function ModalPagamentoVenda({ aberto, onFechar, totalVenda, cliente, tipoMovFiscal, onConfirmar, onClienteChange }) {
  const [etapaSalvar, setEtapaSalvar]         = useState(1);
  const [descontoR, setDescontoR]             = useState('');
  const [descontoP, setDescontoP]             = useState('');
  const [formasPgto, setFormasPgto]           = useState([]);
  const [valorPgtoAtual, setValorPgtoAtual]   = useState('');
  const [formaPgtoSel, setFormaPgtoSel]       = useState('Dinheiro');
  const [subCartao, setSubCartao]             = useState(null); // null | 'choose' | 'debito' | 'credito'
  const [parcelasCartao, setParcelasCartao]   = useState(1);
  const [bandeiras, setBandeiras]             = useState([]);
  const [bandeiraSel, setBandeiraSel]         = useState(null);
  // Código de autorização do cartão (cAut) — opcional, digitado do
  // comprovante da maquininha; enriquece o grupo <card> da NF-e/NFC-e.
  const [codigoAutorizacaoCartao, setCodigoAutorizacaoCartao] = useState('');
  const [pixContaId, setPixContaId]           = useState('');
  const [boletoContaId, setBoletoContaId]     = useState('');
  const [parcelasCrediario, setParcelasCrediario] = useState(1);
  const [parcelasBoleto, setParcelasBoleto]   = useState(1);
  // Só existe aqui (Pedidos/Orçamentos) — o PDV não manda esses campos, então
  // cai no default do backend (1ª parcela em 30 dias, demais de 30 em 30),
  // igual sempre foi. Mesmas 3 regras do lançamento avulso de Contas a
  // Pagar/Receber (ModalNovoTituloParcelado.jsx).
  const [venc1Boleto, setVenc1Boleto]         = useState('');
  const [regraVencBoleto, setRegraVencBoleto] = useState('direto');
  const [nDiasBoleto, setNDiasBoleto]         = useState('');
  const [contas, setContas]                   = useState([]);
  const [salvando, setSalvando]               = useState(false);
  const [erroSalvar, setErroSalvar]           = useState('');
  // Fidelidade: saldo/crédito disponível do cliente atual da venda — mesma
  // lógica do PDV (ver PDV.jsx), refeita aqui porque este modal é autocontido.
  const [fidelidadeCliente, setFidelidadeCliente] = useState(null);
  useEffect(() => {
    if (!aberto || !cliente?.id || cliente.id === 1) { setFidelidadeCliente(null); return; }
    let ativo = true;
    api.get(`/clientes/${cliente.id}/fidelidade`)
      .then(({ data }) => { if (ativo) setFidelidadeCliente(data); })
      .catch(() => { if (ativo) setFidelidadeCliente(null); });
    return () => { ativo = false; };
  }, [aberto, cliente?.id]);
  // Crédito do cliente (gerado por devolução) disponível pra pagar esta
  // venda — mesmo princípio da fidelidade acima, refeito aqui por ser modal
  // autocontido.
  const [creditoCliente, setCreditoCliente] = useState(null);
  useEffect(() => {
    if (!aberto || !cliente?.id || cliente.id === 1) { setCreditoCliente(null); return; }
    let ativo = true;
    api.get('/financeiro/creditos-cliente', { params: { cliente_id: cliente.id } })
      .then(({ data }) => { if (ativo) setCreditoCliente(data); })
      .catch(() => { if (ativo) setCreditoCliente(null); });
    return () => { ativo = false; };
  }, [aberto, cliente?.id]);

  // F10 — escolher cliente sem sair do pagamento (crediário exige cliente)
  const [modalCliente, setModalCliente]       = useState(false);
  const [buscaCliente, setBuscaCliente]       = useState('');
  const [clientes, setClientes]               = useState([]);
  const [buscandoCli, setBuscandoCli]         = useState(false);
  const [subModalNovoCli, setSubModalNovoCli] = useState(false);
  const [novoCliNome, setNovoCliNome]         = useState('');
  const [novoCliCpf, setNovoCliCpf]           = useState('');
  const [novoCliFone, setNovoCliFone]         = useState('');
  const [erroNovoCli, setErroNovoCli]         = useState('');
  const [salvandoNovoCli, setSalvandoNovoCli] = useState(false);

  const descontoRRef   = useRef(null);
  const descontoPRef   = useRef(null);
  const salvarInputRef = useRef(null);
  const dummyRef       = useRef(null); // ModalSalvarVenda refoca buscaRef ao fechar (só existe no PDV)
  const confirmarBtnRef = useRef(null);
  const buscaCliRef    = useRef(null);
  const debounceCliRef = useRef(null);
  const liveRef        = useRef({});

  const bandeirasDebito  = bandeiras.filter(b => (b.tipo || 'ambos') !== 'credito');
  const bandeirasCredito = bandeiras.filter(b => (b.tipo || 'ambos') !== 'debito');

  useEffect(() => {
    if (!bandeiraSel) return;
    const t = bandeiraSel.tipo || 'ambos';
    if ((subCartao === 'debito' && t === 'credito') ||
        (subCartao === 'credito' && t === 'debito')) setBandeiraSel(null);
  }, [subCartao, bandeiraSel]);

  const totalComDesconto = Math.max(0, totalVenda - parsePT(descontoR));
  const totalPago        = formasPgto.reduce((acc, p) => acc + p.valor, 0);
  const troco            = Math.max(0, totalPago - totalComDesconto);
  const restante         = Math.max(0, totalComDesconto - totalPago);
  const semPagamento     = tipoMovFiscal?.gera_financeiro === false;
  // Tolerância de meio centavo: soma de itens em float pode deixar resíduo
  // (ex.: 36.84+65.76 = 102.60000000000001) e travar o Confirmar com resto "0,00"
  const pgtoCompleto     = semPagamento || (totalPago >= totalComDesconto - 0.005 && formasPgto.length > 0);

  // Reset + carga de bandeiras/contas a cada abertura
  useEffect(() => {
    if (!aberto) return;
    setEtapaSalvar(1);
    setDescontoR('');
    setDescontoP('');
    setFormasPgto([]);
    setValorPgtoAtual('');
    setFormaPgtoSel('Dinheiro');
    setSubCartao(null);
    setParcelasCartao(1);
    setBandeiraSel(null);
    setCodigoAutorizacaoCartao('');
    setErroSalvar('');
    setVenc1Boleto(somarDias(hojeBR(), 30));
    setRegraVencBoleto('direto');
    setNDiasBoleto('');
    api.get('/financeiro/bandeiras').then(r => setBandeiras(r.data || [])).catch(() => {});
    api.get('/financeiro/contas').then(r => setContas((r.data || []).filter(c => c.ativo))).catch(() => {});
    setTimeout(() => descontoRRef.current?.focus(), 60);
  }, [aberto]);

  const handleDescontoR = e => {
    const v = e.target.value;
    setDescontoR(v);
    const n = parsePT(v);
    setDescontoP(n > 0 && totalVenda > 0 ? ((n / totalVenda) * 100).toFixed(2) : '');
  };

  const handleDescontoP = e => {
    const v = e.target.value;
    setDescontoP(v);
    const n = parsePT(v);
    setDescontoR(n > 0 ? (totalVenda * n / 100).toFixed(2) : '');
  };

  const avancarParaPgto = () => {
    setEtapaSalvar(2);
    setValorPgtoAtual(totalComDesconto > 0 ? totalComDesconto.toFixed(2) : '');
    setTimeout(() => salvarInputRef.current?.focus(), 60);
  };

  const selecionarDebito = () => setSubCartao('debito');

  const confirmarDebito = () => {
    setFormaPgtoSel('Cartão Débito');
    setSubCartao(null);
    setValorPgtoAtual(restante > 0 ? restante.toFixed(2) : '');
    setTimeout(() => salvarInputRef.current?.focus(), 30);
  };

  // ↑/↓ trocam a bandeira do cartão sem tocar no mouse
  const navegarBandeira = (dir) => {
    const lista = subCartao === 'debito' ? bandeirasDebito : bandeirasCredito;
    if (!lista.length) return;
    const idx = lista.findIndex(b => b.id === bandeiraSel?.id);
    const n = idx < 0 ? 0 : Math.min(Math.max(idx + dir, 0), lista.length - 1);
    setBandeiraSel(lista[n]);
  };

  const selecionarCredito = (parcelas) => {
    setFormaPgtoSel(parcelas === 1 ? 'Cartão Crédito 1x' : `Cartão Crédito ${parcelas}x`);
    setParcelasCartao(parcelas);
    setSubCartao(null);
    setValorPgtoAtual(restante > 0 ? restante.toFixed(2) : '');
    setTimeout(() => salvarInputRef.current?.focus(), 30);
  };

  const adicionarPgto = () => {
    if (subCartao) return; // aguarda seleção de débito/crédito
    const v = parsePT(valorPgtoAtual);
    if (!v || v <= 0) return;
    // Crediário/Boleto exigem cliente identificado — trava aqui (fonte única),
    // não só na UI, porque o atalho de teclado Enter chama esta função direto.
    if ((formaPgtoSel === 'Crediário' || formaPgtoSel === 'Boleto' || formaPgtoSel === 'Pontos Fidelidade' || formaPgtoSel === 'Crédito do Cliente') && (!cliente || cliente.id === 1)) {
      setErroSalvar(`${formaPgtoSel} exige cliente identificado (diferente de VENDA CONSUMIDOR) — pressione F10 para escolher.`);
      return;
    }
    // Boleto com vencimento no passado: o banco (Cora) recusa a cobrança e a
    // venda ficava sem boleto ("Request has invalid parameters").
    if (formaPgtoSel === 'Boleto' && venc1Boleto && venc1Boleto < hojeBR()) {
      setErroSalvar('O vencimento do boleto não pode ser anterior a hoje.');
      return;
    }
    // Venda já paga: nada mais a adicionar
    if (restante <= 0.004) {
      setErroSalvar('A venda já está totalmente paga — não há valor restante.');
      return;
    }
    // Troco só existe em Dinheiro: outras formas não passam do restante
    if (formaPgtoSel !== 'Dinheiro' && v > restante + 0.004) {
      setErroSalvar(`Valor acima do restante (R$ ${restante.toFixed(2).replace('.', ',')}) — troco só em Dinheiro.`);
      return;
    }
    setErroSalvar('');
    // Pagamento que completa a venda → foco no Confirmar (Enter finaliza)
    const completaVenda = totalPago + v >= totalComDesconto - 0.004;
    if (completaVenda) setTimeout(() => confirmarBtnRef.current?.focus(), 60);
    const isPix  = formaPgtoSel === 'PIX';
    const isCred = formaPgtoSel === 'Crediário';
    const isBol  = formaPgtoSel === 'Boleto';
    setFormasPgto(prev => {
      const idx = prev.findIndex(p => p.forma === formaPgtoSel);
      if (idx >= 0 && !isCred && !isBol) {
        const novo = [...prev];
        novo[idx] = { ...novo[idx], valor: parseFloat((novo[idx].valor + v).toFixed(2)) };
        return novo;
      }
      return [...prev, {
        forma:      formaPgtoSel,
        valor:      v,
        bandeira_id: bandeiraSel?.id || null,
        codigo_autorizacao: formaPgtoSel.startsWith('Cartão') ? (codigoAutorizacaoCartao.trim() || null) : null,
        parcelas:   isCred ? (parcelasCrediario || 1) : isBol ? (parcelasBoleto || 1) : (parcelasCartao || 1),
        conta_id:   isPix ? (pixContaId ? parseInt(pixContaId) : null)
                  : isBol ? (boletoContaId ? parseInt(boletoContaId) : null)
                  : null,
        ...(isBol ? {
          venc1: venc1Boleto || null,
          regra_vencimento: regraVencBoleto,
          dias_entre_parcelas: regraVencBoleto === 'ndias' ? (parseInt(nDiasBoleto) || null) : null,
        } : {}),
      }];
    });
    setValorPgtoAtual('');
    setTimeout(() => salvarInputRef.current?.focus(), 30);
  };

  // ── F10 — escolher cliente (mesmos fluxos do ModalCliente do PDV) ──
  const abrirModalCliente = () => {
    setModalCliente(true);
    setBuscaCliente('');
    setClientes([]);
    setSubModalNovoCli(false);
    setErroNovoCli('');
    setTimeout(() => buscaCliRef.current?.focus(), 50);
  };

  const fecharModalCliente = () => {
    setModalCliente(false);
    setBuscaCliente('');
    setClientes([]);
    setSubModalNovoCli(false);
  };

  const handleBuscaClienteChange = e => {
    setBuscaCliente(e.target.value);
    clearTimeout(debounceCliRef.current);
    debounceCliRef.current = setTimeout(async () => {
      if (!e.target.value.trim()) { setClientes([]); return; }
      setBuscandoCli(true);
      try {
        const { data } = await api.get('/clientes', { params: { busca: e.target.value, limite: 12 } });
        setClientes(data);
      } catch { setClientes([]); }
      finally { setBuscandoCli(false); }
    }, 300);
  };

  const selecionarCliente = c => {
    onClienteChange?.(c);
    fecharModalCliente();
    setTimeout(() => salvarInputRef.current?.focus(), 60);
  };

  const limparCliente = () => {
    onClienteChange?.(null);
    fecharModalCliente();
  };

  const salvarNovoCli = async () => {
    if (!novoCliNome.trim()) { setErroNovoCli('Nome é obrigatório.'); return; }
    setSalvandoNovoCli(true); setErroNovoCli('');
    try {
      const { data } = await api.post('/clientes', {
        nome: novoCliNome.trim(),
        cpf_cnpj: novoCliCpf.trim() || undefined,
        fone1: novoCliFone.trim() || undefined,
      });
      selecionarCliente(data);
      setNovoCliNome(''); setNovoCliCpf(''); setNovoCliFone('');
    } catch (err) {
      setErroNovoCli(err.response?.data?.erro || err.response?.data?.erros?.[0]?.msg || 'Erro ao salvar cliente.');
    } finally {
      setSalvandoNovoCli(false);
    }
  };

  const confirmarPagamento = async () => {
    if (salvando) return;
    setSalvando(true);
    setErroSalvar('');
    try {
      await onConfirmar({
        desconto:   parseFloat(parsePT(descontoR).toFixed(2)),
        total_pago: totalPago,
        troco:      troco,
        pagamentos: formasPgto.map(p => ({
          forma_pagamento: p.forma,
          valor:           p.valor,
          bandeira_id:     p.bandeira_id || null,
          codigo_autorizacao: p.codigo_autorizacao || null,
          parcelas:        p.parcelas || 1,
          conta_id:        p.conta_id   || null,
          ...(p.forma === 'Boleto' ? {
            venc1: p.venc1 || null,
            regra_vencimento: p.regra_vencimento || 'direto',
            dias_entre_parcelas: p.dias_entre_parcelas || null,
          } : {}),
        })),
      });
    } catch (err) {
      setErroSalvar(err?.response?.data?.erro || err?.message || 'Erro ao salvar venda. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  liveRef.current = {
    etapaSalvar, subCartao, restante, pgtoCompleto, salvando, modalCliente, fidelidadeCliente, creditoCliente,
    adicionarPgto, selecionarDebito, confirmarDebito, avancarParaPgto, confirmarPagamento, onFechar,
    selecionarCredito, navegarBandeira, abrirModalCliente, fecharModalCliente,
  };

  // Atalhos de teclado — capture phase, mesmo comportamento do F9 do PDV
  useEffect(() => {
    if (!aberto) return;
    const handler = e => {
      // Diálogo de senha de administrador aberto → nenhum atalho vale
      if (document.querySelector('[data-senha-admin]')) return;

      const L = liveRef.current;
      // Modal de cliente (F10) aberto por cima: Escape fecha só ele; as demais
      // teclas vão para a busca de cliente (sem os atalhos b/c/d/h/n/p do pagamento)
      if (L.modalCliente) {
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); L.fecharModalCliente(); }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation();
        if (L.salvando) return;
        if (L.subCartao) setSubCartao(null);
        else L.onFechar();
        return;
      }
      // F10 → escolher/trocar cliente sem sair do pagamento
      if (e.key === 'F10' && onClienteChange) {
        e.preventDefault(); e.stopPropagation();
        L.abrirModalCliente();
        return;
      }
      // Etapa 1 — desconto
      if (L.etapaSalvar === 1) {
        const tag = document.activeElement?.tagName;
        if (e.key === 'Enter' && tag !== 'INPUT') { e.preventDefault(); L.avancarParaPgto(); return; }
        if (!e.ctrlKey && !e.altKey && tag !== 'INPUT' && e.key.toLowerCase() === 's') {
          e.preventDefault(); descontoRRef.current?.focus();
        }
        return;
      }
      // Etapa 2 — pagamento
      if (e.key === 'F9') {
        e.preventDefault(); e.stopPropagation();
        if (L.pgtoCompleto) L.confirmarPagamento();
        return;
      }
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.key === 'Backspace' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        setEtapaSalvar(1);
        setTimeout(() => descontoRRef.current?.focus(), 60);
        return;
      }
      if (L.subCartao === 'choose') {
        if (e.key.toLowerCase() === 'd') { e.preventDefault(); L.selecionarDebito(); return; }
        if (e.key.toLowerCase() === 'c') { e.preventDefault(); setSubCartao('credito'); return; }
        return;
      }
      // Bandeira por teclado nos dois sub-fluxos (↑/↓)
      if ((L.subCartao === 'debito' || L.subCartao === 'credito') &&
          (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        L.navegarBandeira(e.key === 'ArrowDown' ? 1 : -1);
        return;
      }
      if (L.subCartao === 'debito') {
        if (e.key === 'Enter') { e.preventDefault(); L.confirmarDebito(); }
        return;
      }
      if (L.subCartao === 'credito') {
        // 1–9 escolhem as parcelas (0 = 10x)
        if (/^[0-9]$/.test(e.key)) {
          e.preventDefault();
          L.selecionarCredito(e.key === '0' ? 10 : parseInt(e.key, 10));
        }
        return;
      }
      if (e.key === 'Enter' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        if (L.pgtoCompleto) L.confirmarPagamento(); else L.adicionarPgto();
        return;
      }
      if (e.key.toLowerCase() === 'c') { e.preventDefault(); setSubCartao('choose'); return; }
      const formaMap = { b: 'Boleto', d: 'Dinheiro', h: 'Cheque', n: 'Crediário', p: 'PIX',
        ...(L.fidelidadeCliente?.credito_disponivel > 0 ? { f: 'Pontos Fidelidade' } : {}),
        ...(L.creditoCliente?.total > 0 ? { v: 'Crédito do Cliente' } : {}) };
      const forma = formaMap[e.key?.toLowerCase()];
      if (forma) {
        e.preventDefault();
        setFormaPgtoSel(forma);
        setSubCartao(null);
        const valorMax = forma === 'Pontos Fidelidade'
          ? Math.min(L.restante, L.fidelidadeCliente.credito_disponivel)
          : forma === 'Crédito do Cliente'
          ? Math.min(L.restante, L.creditoCliente.total)
          : L.restante;
        setValorPgtoAtual(valorMax > 0 ? valorMax.toFixed(2) : '');
        setTimeout(() => salvarInputRef.current?.focus(), 30);
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [aberto]);

  return (
    <>
    <ModalSalvarVenda
      modalSalvar={aberto}
      setModalSalvar={v => { if (!v && !salvando) onFechar(); }}
      buscaRef={dummyRef}
      etapaSalvar={etapaSalvar}
      setEtapaSalvar={setEtapaSalvar}
      totalVenda={totalVenda}
      totalComDesconto={totalComDesconto}
      descontoR={descontoR}
      descontoP={descontoP}
      handleDescontoR={handleDescontoR}
      handleDescontoP={handleDescontoP}
      descontoRRef={descontoRRef}
      descontoPRef={descontoPRef}
      avancarParaPgto={avancarParaPgto}
      semPagamento={semPagamento}
      tipoMovFiscal={tipoMovFiscal}
      formasPgto={formasPgto}
      setFormasPgto={setFormasPgto}
      restante={restante}
      subCartao={subCartao}
      setSubCartao={setSubCartao}
      selecionarDebito={selecionarDebito}
      confirmarDebito={confirmarDebito}
      selecionarCredito={selecionarCredito}
      bandeirasDebito={bandeirasDebito}
      bandeirasCredito={bandeirasCredito}
      bandeiraSel={bandeiraSel}
      setBandeiraSel={setBandeiraSel}
      codigoAutorizacaoCartao={codigoAutorizacaoCartao}
      setCodigoAutorizacaoCartao={setCodigoAutorizacaoCartao}
      parcelasCartao={parcelasCartao}
      formaPgtoSel={formaPgtoSel}
      setFormaPgtoSel={setFormaPgtoSel}
      salvarInputRef={salvarInputRef}
      valorPgtoAtual={valorPgtoAtual}
      setValorPgtoAtual={setValorPgtoAtual}
      adicionarPgto={adicionarPgto}
      pixContaId={pixContaId}
      setPixContaId={setPixContaId}
      boletoContaId={boletoContaId}
      setBoletoContaId={setBoletoContaId}
      contasAbertura={contas}
      cliente={cliente}
      fidelidadeCliente={fidelidadeCliente}
      creditoCliente={creditoCliente}
      parcelasCrediario={parcelasCrediario}
      setParcelasCrediario={setParcelasCrediario}
      parcelasBoleto={parcelasBoleto}
      setParcelasBoleto={setParcelasBoleto}
      venc1Boleto={venc1Boleto}
      setVenc1Boleto={setVenc1Boleto}
      regraVencBoleto={regraVencBoleto}
      setRegraVencBoleto={setRegraVencBoleto}
      nDiasBoleto={nDiasBoleto}
      setNDiasBoleto={setNDiasBoleto}
      boletoParcelamentoAvancado
      totalPago={totalPago}
      troco={troco}
      erroSalvar={erroSalvar}
      pgtoCompleto={pgtoCompleto}
      salvando={salvando}
      confirmarPagamento={confirmarPagamento}
      confirmarBtnRef={confirmarBtnRef}
      // "online" só existe de verdade no PDV (modo offline com fila local);
      // este modal (Pedidos/Orçamentos etc.) sempre fala direto com a API,
      // então sem isso os botões de Pontos Fidelidade/Crédito do Cliente
      // (condicionados a `online &&` no ModalSalvarVenda) ficavam escondidos.
      online
    />
    {onClienteChange && (
      <ModalCliente
        modalCliente={modalCliente}
        setModalCliente={v => { if (!v) fecharModalCliente(); else setModalCliente(true); }}
        buscaRef={dummyRef}
        buscaCliRef={buscaCliRef}
        buscaCliente={buscaCliente}
        handleBuscaClienteChange={handleBuscaClienteChange}
        buscandoCli={buscandoCli}
        clientes={clientes}
        selecionarCliente={selecionarCliente}
        subModalNovoCli={subModalNovoCli}
        setSubModalNovoCli={setSubModalNovoCli}
        novoCliNome={novoCliNome}
        setNovoCliNome={setNovoCliNome}
        novoCliCpf={novoCliCpf}
        setNovoCliCpf={setNovoCliCpf}
        novoCliFone={novoCliFone}
        setNovoCliFone={setNovoCliFone}
        erroNovoCli={erroNovoCli}
        setErroNovoCli={setErroNovoCli}
        salvarNovoCli={salvarNovoCli}
        salvandiNovoCli={salvandoNovoCli}
        limparCliente={limparCliente}
      />
    )}
    </>
  );
}
